import { FC, ReactElement, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { SearchConsoleSitemapDomainsQueryKeys, useCheckByIdSearchConsoleSitemapDomainsQuery, useGetByIdSearchConsoleSitemapDomainsQuery, useSubmitSitemapToAccountMutation } from '@/modules/Domains';

import { SearchConsoleSitemapDossierView } from './SearchConsoleSitemapDossierView';

interface Props {
  siteId: string;
  domainId: string;
}

export const SearchConsoleSitemapDossierContainer: FC<Props> = memo(({ domainId, siteId }) => {
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [sitemapContent, setSitemapContent] = useState<typeof searchConsoleDomainSitemapData | null>(null);

  const {
    error: searchConsoleDomainSitemapError,
    isError: isSearchConsoleDomainSitemapError,
    isFetching: isSearchConsoleDomainSitemapFetching,
  } = useCheckByIdSearchConsoleSitemapDomainsQuery(
    siteId,
    domainId
  )({
    enabled: !!domainId && !!siteId,
    onSettled: () => {
      setSitemapContent(null);
    },
    onError: async () => {
      await queryClient.refetchQueries([SearchConsoleSitemapDomainsQueryKeys.GetAll, siteId], { exact: false });
    },
  });

  const {
    mutate: refetchSearchConsoleDomainSitemap,
    data: searchConsoleDomainSitemapData,
    isLoading: isSearchConsoleDomainSitemapLoading,
  } = useGetByIdSearchConsoleSitemapDomainsQuery(
    siteId,
    domainId
  )({
    onSuccess: (data) => {
      setSitemapContent(data || null);
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  const {
    mutate: submitSitemapMutation,
    isLoading: isSubmitSitemapMutationLoading
  } = useSubmitSitemapToAccountMutation(siteId, domainId)({
    onSuccess: () => {
      appMessageApi.success('Sitemap було завантажено в Search Console успішно!');

      handleGetSitemap();
    },
    onError: async (error) => {
      messageOnError(error, appMessageApi);

      await queryClient.refetchQueries([SearchConsoleSitemapDomainsQueryKeys.GetAll, siteId], { exact: false });
    }
  });

  const handleGetSitemap = () => {
    refetchSearchConsoleDomainSitemap();
  };

  const handleSubmitSitemap = useCallback(() => {
    submitSitemapMutation();
  }, []);

  return (
    <FallbackedLayout
      isLoading={ isSearchConsoleDomainSitemapFetching }
      isError={ isSearchConsoleDomainSitemapError }
      error={ searchConsoleDomainSitemapError }
    >
      {
        (): ReactElement => (
          <SearchConsoleSitemapDossierView
            searchConsoleDomainSitemap={ sitemapContent || null }
            isSitemapDataRenewing={ isSearchConsoleDomainSitemapLoading }
            isSitemapSubmitting={ isSubmitSitemapMutationLoading }
            isSearchConsoleDomainSitemapLoading={ isSearchConsoleDomainSitemapLoading }
            handleGetSitemap={ handleGetSitemap }
            handleGenerateSitemap={ handleSubmitSitemap }
          />
        )
      }
    </FallbackedLayout>
  );
});
